import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { List, ListItem } from 'chaoskit/src/components';

import BaseLayout from '../../layouts/BaseLayout';
import Hero from '../../partials/Hero';
import FooterCTA from '../../partials/footer/FooterCTA';
import StaticFooter from '../../partials/footer/StaticFooter';
import StaticFeaturedArticles from '../../partials/global/StaticFeaturedArticles';
import { Icon, Link, Data, WebinarForm } from '../../components';

const StyledHeading = ({ level, className, children, ...rest }) => {
  const Heading = `h${level}`;
  const classes = cx(
    'u-textMuted u-textNormal u-textUppercase u-textSpace',
    className
  );

  return (
    <Heading className={classes} {...rest}>
      {children}
    </Heading>
  );
};

StyledHeading.propTypes = {
  level: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

const UpcomingWebinar = ({ node }) => (
  <Link className="webinars__actionCard" to={node.fields.fullUrl}>
    <div className="webinars__actionCard-top">
      {(node.frontmatter.onDemand || node.frontmatter.date) && (
        <StyledHeading level="5" className="u-textRegular">
          {node.frontmatter.onDemand
            ? 'On-Demand'
            : node.frontmatter.date &&
              moment(node.frontmatter.date).format('dddd, MMM D, YYYY')}
        </StyledHeading>
      )}

      <h4 className="u-mt--regular">
        <Data>{node.frontmatter.hero.heading}</Data>
      </h4>
      <div className="u-textSmall u-textDefault">
        <Data>{node.excerpt}</Data>
      </div>
    </div>

    <div className="u-textHeading u-textPrimary webinars__actionCard-more">
      Read More
      <Icon icon="arrow-right" className="u-ml--small" />
    </div>
  </Link>
);

UpcomingWebinar.propTypes = {
  node: PropTypes.object.isRequired,
};

const WebinarSingle = ({
  data: {
    pageData: {
      html,
      frontmatter: {
        title,
        description,
        onDemand,
        time,
        date,
        hero: { heading, blurb, meta, form },
        agenda,
        speakers,
      },
      fields: { slug, fullUrl },
    },
    upcomingWebinars: { edges: upcomingWebinars },
  },
  pageContext: { nodeType },
}) => (
  <BaseLayout noFooter headerProps={{ sticky: false, showNav: false }}>
    <Helmet
      title={title}
      meta={[
        { name: 'description', content: description },
        { name: 'node-type', content: nodeType },
      ]}
    />
    <Hero
      contrast={false}
      className="u-gradient--teal-brightTeal"
      particles={false}
    >
      <div className="hero-content">
        <div className="container">
          <div className="row row--xlargeGutter@medium u-flexMiddle">
            <div className="column-6@medium u-contrast u-textCenter u-textLeft@medium">
              <StyledHeading level="4" className="u-textFluid--xlarge">
                Webinar
              </StyledHeading>
              <h1 className="hero-title u-textFluid--h2-h3 u-mt--remove">
                <Data>{heading}</Data>
              </h1>
              <div className="hero-titleSub">
                <Data markdown>{blurb}</Data>
              </div>
              {onDemand ? (
                <div className="u-textBold u-textHeading u-textLarge u-mt--large">
                  On-Demand
                </div>
              ) : (
                date &&
                time && (
                  <Fragment>
                    <StyledHeading
                      level="5"
                      className="u-textMedium u-mt--large u-mb--small"
                    >
                      Time
                    </StyledHeading>
                    <time
                      dateTime={moment(date).format('dddd, MMM D, YYYY')}
                      className="u-textBold u-textHeading u-textLarge"
                    >
                      {moment(date).format('dddd, MMM D, YYYY')}
                      <br />
                      <Data>{time}</Data>
                    </time>
                  </Fragment>
                )
              )}
              {meta && (
                <div className="u-textSmall u-mt--small">
                  <Data>{meta}</Data>
                </div>
              )}
            </div>
            <div className="column-6@medium">
              <div id="form" className="hero-formWrapper">
                {form.heading && (
                  <h3 className="u-textFluid--xlarge u-mb--large u-textCenter u-textLeft@medium">
                    <Data>{form.heading}</Data>
                  </h3>
                )}
                <WebinarForm
                  formId={slug}
                  currentUrl={fullUrl}
                  onDemand={onDemand}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Hero>
    <section className="section section--large">
      <div className="container">
        <div className="row row--xlargeGutter row--deepMatch">
          <div className="column-6@medium">
            <div className="webinars__columnContent">
              <StyledHeading level="5" className="u-textMedium u-mb--large">
                About This Webinar
              </StyledHeading>
              <div
                className="u-textFluid--large"
                dangerouslySetInnerHTML={{ __html: html }}
              />
              <StyledHeading
                level="5"
                className="u-textMedium u-mt--xlarge u-mb--large"
              >
                Agenda
              </StyledHeading>
              <List className="u-list--bullets">
                {agenda.map(({ item }) => (
                  <ListItem key={item}>
                    <Data markdown>{item}</Data>
                  </ListItem>
                ))}
              </List>
            </div>
          </div>
          <div className="column-6@medium">
            <div className="webinars__columnContent is-right">
              <StyledHeading level="5" className="u-textMedium u-mb--large">
                About The Speakers
              </StyledHeading>
              <aside>
                {speakers.map(speaker => (
                  <div className="webinars__speaker" key={speaker.name}>
                    <header className="webinars__speaker-header">
                      <div
                        className="webinars__speaker-header-avatar"
                        style={{ backgroundImage: `url(${speaker.image})` }}
                      />
                      <div>
                        <h4 className="u-mb--small">
                          <Data>{speaker.name}</Data>
                        </h4>
                        <div className="u-textHeading u-textNormal u-textMuted u-textMedium">
                          <Data>{speaker.title}</Data>
                        </div>
                        <div className="u-textHeading u-textNormal u-textMedium u-textTeal u-mt--xsmall">
                          <Data>{speaker.company}</Data>
                        </div>
                      </div>
                    </header>
                    <Data markdown>{speaker.blurb}</Data>
                  </div>
                ))}
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
    {upcomingWebinars.length > 0 && (
      <section className="section u-gradient--teal-brightTeal">
        <div className="container">
          <div className="section-titleWrapper u-contrast">
            <h3 className="section-title">
              Check out other webinars from Gremlin
            </h3>
          </div>
          <ul className="blockGrid blockGrid--deepMatch blockGrid-3@medium u-flexCenter">
            {upcomingWebinars.map(({ node }) => (
              <li key={node.fileAbsolutePath}>
                <UpcomingWebinar node={node} />
              </li>
            ))}
          </ul>
        </div>
      </section>
    )}
    <StaticFeaturedArticles
      className={upcomingWebinars.length > 0 ? '' : 'u-borderTop'}
    />
    <FooterCTA />
    <StaticFooter />
  </BaseLayout>
);

WebinarSingle.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
};

export default WebinarSingle;

export const pageQuery = graphql`
  query WebinarSingle(
    $slug: String!
    $fileAbsolutePath: String!
    $date: Date!
  ) {
    pageData: markdownRemark(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/webinars/" }
    ) {
      ...WebinarFragment
    }

    upcomingWebinars: allMarkdownRemark(
      limit: 3
      sort: { fields: [frontmatter___date], order: ASC }
      filter: {
        fileAbsolutePath: { regex: "/src/webinars/", ne: $fileAbsolutePath }
        frontmatter: { title: { ne: "BLUEPRINT" }, date: { gte: $date } }
      }
    ) {
      edges {
        node {
          ...WebinarFragment
        }
      }
    }
  }
`;
